import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";

import { useGetNcsTimeseriesQuery } from "../../../../store/appApi";
import { panelClass } from "./config";

const GRAINS = [
  { key: "month", label: "MoM" },
  { key: "quarter", label: "QoQ" },
  { key: "year", label: "YoY" },
];

// How many trailing periods to show per grain — keeps the bars readable
// instead of cramming ~70 months into one narrow card.
const WINDOW = { month: 13, quarter: 8, year: 6 };

// "2026-03" → "Mar '26" ; "2026-Q1" → "Q1 '26" ; "2026" → "2026"
const prettyPeriod = (p = "") => {
  const mo = /^(\d{4})-(\d{2})$/.exec(p);
  if (mo) {
    const d = new Date(Date.UTC(Number(mo[1]), Number(mo[2]) - 1, 1));
    return `${d.toLocaleString("en-US", { month: "short", timeZone: "UTC" })} '${mo[1].slice(2)}`;
  }
  const q = /^(\d{4})-Q([1-4])$/.exec(p);
  if (q) return `Q${q[2]} '${q[1].slice(2)}`;
  return p;
};

const buildOptions = (categories, color) => {
  const few = categories.length <= 9;
  return {
    chart: {
      type: "bar",
      height: 240,
      toolbar: { show: false },
      fontFamily: "inherit",
      animations: { enabled: false },
      parentHeightOffset: 0,
    },
    plotOptions: {
      bar: {
        borderRadius: 3,
        columnWidth: few ? "45%" : "68%",
        dataLabels: { position: "top" },
      },
    },
    colors: [color],
    dataLabels: {
      enabled: few,
      offsetY: -16,
      style: { fontSize: "10px", colors: ["#64748b"], fontWeight: 600 },
      formatter: (v) => (v ? v : ""),
    },
    grid: {
      borderColor: "#eef2f7",
      strokeDashArray: 4,
      padding: { top: 8, right: 4, bottom: 0, left: 4 },
    },
    xaxis: {
      categories,
      tickPlacement: "on",
      labels: {
        style: { fontSize: "11px", colors: "#94a3b8" },
        rotate: -45,
        rotateAlways: categories.length > 6,
        hideOverlappingLabels: true,
        trim: false,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { fontSize: "11px", colors: "#94a3b8" },
        formatter: (v) => Math.round(v),
      },
      min: 0,
      forceNiceScale: true,
    },
    tooltip: { theme: "light", y: { formatter: (v) => `${v}` } },
    legend: { show: false },
  };
};

const MiniChart = ({ title, categories, data, color }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4">
    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {title}
    </div>
    {categories.length === 0 ? (
      <div className="py-14 text-center text-xs text-gray-400">No data yet</div>
    ) : (
      <ReactApexChart
        options={buildOptions(categories, color)}
        series={[{ name: title, data }]}
        type="bar"
        height={240}
      />
    )}
  </div>
);

const TrendCharts = ({ partnerCode }) => {
  const [grain, setGrain] = useState("month");
  const { data, isFetching } = useGetNcsTimeseriesQuery({ partnerCode, grain });

  const shown = useMemo(() => {
    const series = data?.series || [];
    return series.slice(-(WINDOW[grain] || 12));
  }, [data, grain]);

  const cats = shown.map((s) => prettyPeriod(s.period));

  return (
    <section className={panelClass}>
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Trends{" "}
          <span className="ml-1 text-xs font-normal normal-case text-gray-400">
            {isFetching
              ? "…"
              : `last ${shown.length} ${
                  grain === "month" ? "months" : grain === "quarter" ? "quarters" : "years"
                }`}
          </span>
        </h2>
        <div className="inline-flex rounded-xl border border-gray-300 p-0.5">
          {GRAINS.map((g) => (
            <button
              key={g.key}
              onClick={() => setGrain(g.key)}
              className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
                grain === g.key
                  ? "bg-cyan-600 text-white"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 p-5 lg:grid-cols-3">
        <MiniChart
          title="New connections"
          categories={cats}
          data={shown.map((s) => s.newConnections || 0)}
          color="#0891b2"
        />
        <MiniChart
          title="Went inactive (lapsed)"
          categories={cats}
          data={shown.map((s) => s.wentInactive || 0)}
          color="#d97706"
        />
        <MiniChart
          title="Recharged (recovered + renewed)"
          categories={cats}
          data={shown.map((s) => s.recharged || 0)}
          color="#16a34a"
        />
      </div>
    </section>
  );
};

export default TrendCharts;
